import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Observable, Subscription } from 'rxjs';

import { RequestStatus } from 'app/components/request-status';
import { SeamRequestLink } from 'app/components/seam-request-link';
import { Translate } from 'app/components/translate';
import { Role } from 'app/constants/role';
import { SeamPattern } from 'app/model/seam-pattern';
import { SeamRequest } from 'app/model/seam-request';
import { SeamRequestFilter } from 'app/model/seam-request-filter';
import { RootState } from 'app/store';
import { Formatters } from 'app/utils/formatters';
import { Strings } from 'app/utils/strings';

import { selectRoles } from 'features/auth/auth.slice';

import { PatternAccordion } from './request/pattern-accordion';
import { fetchSeamRequests, loadSeamPatterns, selectRequest } from './slice';

interface RequestTableProps extends RouteComponentProps {
    filter: SeamRequestFilter;
    refreshTrigger?: number;
    // redux Props
    currentRoles: Record<string, boolean>;
    fetchSeamRequests: (filter: SeamRequestFilter) => Observable<SeamRequest[]>;
    selectRequest: (request: SeamRequest) => void;
    loadSeamPatterns: (request: SeamRequest) => Observable<SeamPattern[]>;
}

interface RequestTableState {
    requests: SeamRequest[];
    expandedId: string;
    expandedPatterns: Record<string, SeamPattern[] | undefined>;
}

class RequestTable extends React.Component<RequestTableProps, RequestTableState> {
    private subscription?: Subscription;

    public constructor(props: Readonly<RequestTableProps> | RequestTableProps) {
        super(props);

        this.state = {
            requests: [],
            expandedId: '',
            expandedPatterns: {},
        };
    }

    public componentDidMount() {
        this.props.fetchSeamRequests(this.props.filter).subscribe((requests) => {
            this.setState({ requests });
        });
    }

    public componentDidUpdate(prevProps: Readonly<RequestTableProps>) {
        if (prevProps.refreshTrigger !== this.props.refreshTrigger) {
            this.componentDidMount();
        }
    }

    public componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public openRequest(request: SeamRequest) {
        this.props.selectRequest(request);
    }

    public render() {
        return (
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Request</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Store</TableCell>
                            <TableCell>Brand</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell>Assignee</TableCell>
                            <TableCell>Manager</TableCell>
                            {/* <TableCell>Tuner</TableCell>*/}
                            {/* <TableCell>% Labeled</TableCell>*/}
                        </TableRow>
                    </TableHead>
                    <TableBody>{this.mapTableRows()}</TableBody>
                </Table>
            </TableContainer>
        );
    }

    private mapTableRows() {
        const columnCount = 7;
        if (0 === this.state.requests.length) {
            return (
                <TableRow>
                    <TableCell colSpan={columnCount} align='center'>
                        (None)
                    </TableCell>
                </TableRow>
            );
        }

        return this.state.requests.map((row) => {
            // const progress = CalcHelpers.getCompletionPercent(row);
            const store = row.store;

            return (
                <React.Fragment key={row.id}>
                    <TableRow hover onClick={() => this.patternInfo(row)}>
                        <TableCell>
                            <SeamRequestLink id={row.id} onClick={() => this.openRequest(row)}>
                                (<Translate tkey={`headings:${Strings.seamRequestType(row)}`} />)
                            </SeamRequestLink>
                        </TableCell>
                        <TableCell>
                            <RequestStatus status={row.currentPlace} />
                        </TableCell>
                        <TableCell>{store.name}</TableCell>
                        <TableCell>{store.brand?.name}</TableCell>
                        <TableCell>{row.dueDate ? format(parseISO(row.dueDate), 'MM/dd/yyyy') : ''}</TableCell>
                        <TableCell>
                            {this.props.currentRoles[Role.CRIS_USER]
                                ? Formatters.userDisplayName(row.owner)
                                : 'Tagging Team'}
                        </TableCell>
                        <TableCell>{Formatters.userDisplayName(row.manager)}</TableCell>
                        {/* <TableCell>{row.tuner?.username}</TableCell>*/}
                        {/* <TableCell>*/}
                        {/*    <Box display='flex' alignItems='center'>*/}
                        {/*        <Box className='mr-1'>{progress}%</Box>*/}
                        {/*        <Box flexGrow={1}>*/}
                        {/*            <LinearProgress variant='determinate' value={progress}/>*/}
                        {/*        </Box>*/}
                        {/*    </Box>*/}
                        {/* </TableCell>*/}
                    </TableRow>
                    {row.id === this.state.expandedId && (
                        <TableRow>
                            <TableCell colSpan={9999}>
                                {RequestTable.renderPattenrs(this.state.expandedPatterns[row.id])}
                            </TableCell>
                        </TableRow>
                    )}
                </React.Fragment>
            );
        });
    }

    private patternInfo(row: SeamRequest) {
        if (row.id === this.state.expandedId) {
            return;
        }

        this.setState({ expandedId: row.id });

        if (undefined !== this.state.expandedPatterns[row.id]) {
            return;
        }

        this.subscription = this.props.loadSeamPatterns(row).subscribe((value) => {
            this.setState({
                expandedPatterns: {
                    ...this.state.expandedPatterns,
                    [row.id]: value,
                },
            });
        });
    }

    private static renderPattenrs(expandedPattern: SeamPattern[] | undefined) {
        if (undefined === expandedPattern) {
            return 'Loading...';
        }

        return expandedPattern.length
            ? expandedPattern.map((pattern, key) => (
                  <PatternAccordion key={key} pattern={pattern} editable={false} sequence={key + 1} />
              ))
            : 'No patterns yet.';
    }
}

const mapStateToProps = (state: RootState) => ({
    currentRoles: selectRoles(state),
});

const connected = connect(mapStateToProps, {
    fetchSeamRequests,
    selectRequest: selectRequest as ActionCreatorWithPayload<SeamRequest>,
    loadSeamPatterns,
})(withRouter(RequestTable));

export { connected as RequestTable };
